import type { StepAcquisitionSequencePoint } from "api/types/charts";
import type { StepBreakdownRow } from "../Targets/TargetStepBreakdownChart";
import { compareNumericStringKeys } from "./compareNumericStringKeys";

/**
 * Shapes a task-analysis step acquisition sequence into the Gantt rows the
 * step breakdown chart renders. Returns null when there is no sequence data.
 * Shared by TargetChartArea and the doc-gen GoalProgressCard so the two
 * surfaces cannot drift.
 */
export function buildStepAcquisitionRows(
  stepAcquisitionSequence: StepAcquisitionSequencePoint[] | undefined,
  totalSessions: number
): StepBreakdownRow[] | null {
  if (!stepAcquisitionSequence || stepAcquisitionSequence.length === 0) return null;

  return [...stepAcquisitionSequence]
    .sort((a, b) => compareNumericStringKeys(a.step_id, b.step_id))
    .map((step) => {
      const firstCorrect = step.first_correct_session_number;
      const firstIndependent = step.first_independent_session_number;
      const start = firstCorrect ?? firstIndependent ?? 1;
      const end = firstIndependent ?? totalSessions;
      const parsedStepId = Number(step.step_id);

      return {
        step: `Step ${step.step_id}`,
        stepNumber: isFinite(parsedStepId) ? parsedStepId : 0,
        startSession: start,
        endSession: Math.max(start, end),
        status:
          firstIndependent != null ? "mastered" : firstCorrect != null ? "emerging" : "not-started",
      };
    });
}
